import * as React from "react";
import { useState } from "react";
import { Formik, Form, useField } from "formik";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { Modal, Input, Button } from "components/bootstrap";
import { FormikInput, ModalSubmit, Spinner, Icon, DatePicker } from "components/common";
import Popover from "components/common/Popover";
import Select from "components/common/Select";
import { isValidURL, hasAcceptedProtocol } from "util/URLUtils";
import { DATE_TIME_FORMATS } from "util/DateTime";
import { fetchAllEventDefinitions } from "components/event-definitions/hooks/useEventDefinitions";
import useAllStreams from "components/streams/hooks/useAllStreams";
import streamCategoryName from "logic/streams/streamCategoryName";
import { useCreateDetectionChain, useUpdateDetectionChain } from "security-app/hooks/useDetectionChainsAPI";
const VALUE_DELIMITER = "";
const isValidDateInput = (value) => moment(value, DATE_TIME_FORMATS.date, true).isValid();
const toDateString = (day) => [day.getFullYear(), `${day.getMonth() + 1}`.padStart(2, "0"), `${day.getDate()}`.padStart(2, "0")].join("-");
const initialValues = (chain) => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j;
  return {
    name: (_a = chain == null ? void 0 : chain.name) != null ? _a : "",
    description: (_b = chain == null ? void 0 : chain.description) != null ? _b : "",
    threat_actor: (_c = chain == null ? void 0 : chain.threat_actor) != null ? _c : "",
    targeted_technologies: (_d = chain == null ? void 0 : chain.targeted_technologies) != null ? _d : [],
    industries: (_e = chain == null ? void 0 : chain.industries) != null ? _e : [],
    stream_categories: (_f = chain == null ? void 0 : chain.stream_categories) != null ? _f : [],
    source: (_g = chain == null ? void 0 : chain.source) != null ? _g : "",
    reference_url: (_h = chain == null ? void 0 : chain.reference_url) != null ? _h : "",
    created_at: (_i = chain == null ? void 0 : chain.created_at) != null ? _i : "",
    event_definition_ids: (_j = chain == null ? void 0 : chain.event_definition_ids) != null ? _j : []
  };
};
const StringListField = ({
  name,
  label,
  help,
  placeholder,
  suggestions = []
}) => {
  var _a;
  const [field, , helpers] = useField(name);
  const value = (_a = field.value) != null ? _a : [];
  const optionValues = [.../* @__PURE__ */ new Set([...suggestions, ...value])];
  return /* @__PURE__ */ React.createElement(Input, { id: name, label, help }, /* @__PURE__ */ React.createElement(
    Select,
    {
      inputId: `${name}-select`,
      multi: true,
      allowCreate: true,
      delimiter: VALUE_DELIMITER,
      options: optionValues.map((v) => ({
        label: name === "stream_categories" ? streamCategoryName(v) : v,
        value: v
      })),
      value: value.join(VALUE_DELIMITER),
      placeholder,
      onChange: (values) => helpers.setValue(
        values ? values.split(VALUE_DELIMITER).map((v) => v.trim()).filter(Boolean) : []
      )
    }
  ));
};
const CreatedDateField = () => {
  var _a;
  const [field, meta, helpers] = useField("created_at");
  const [open, setOpen] = useState(false);
  const value = (_a = field.value) != null ? _a : "";
  const onDayChange = (day) => {
    helpers.setValue(toDateString(day));
    setOpen(false);
  };
  return /* @__PURE__ */ React.createElement(Popover, { opened: open, position: "bottom-start", withArrow: true, onClose: () => setOpen(false) }, /* @__PURE__ */ React.createElement(Popover.Target, null, /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(
    Input,
    {
      type: "text",
      id: "created_at",
      name: "created_at",
      label: "Source Created Date",
      help: "Date the chain was authored at the source.",
      error: meta.touched && meta.error ? meta.error : void 0,
      placeholder: DATE_TIME_FORMATS.date,
      autoComplete: "off",
      value,
      onChange: (e) => helpers.setValue(e.target.value),
      onBlur: () => helpers.setTouched(true),
      buttonAfter: /* @__PURE__ */ React.createElement(Button, { title: "Pick a date", onClick: () => setOpen((prev) => !prev) }, /* @__PURE__ */ React.createElement(Icon, { name: "calendar_month" }))
    }
  ))), /* @__PURE__ */ React.createElement(Popover.Dropdown, { title: "Pick a date" }, /* @__PURE__ */ React.createElement(DatePicker, { date: isValidDateInput(value) ? value : void 0, onChange: onDayChange })));
};
const EventDefinitionsField = () => {
  var _a;
  const [field, , helpers] = useField("event_definition_ids");
  const { data: options = [], isLoading } = useQuery({
    queryKey: ["event-definitions", "all", "detection-chain-select"],
    queryFn: () => fetchAllEventDefinitions().then(
      (res) => res.event_definitions.map((eventDefinition) => ({ label: eventDefinition.title, value: eventDefinition.id }))
    )
  });
  return /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "event_definition_ids",
      label: "Event Definitions",
      help: "The event definitions that make up this detection chain."
    },
    isLoading ? (
      // Render only once the options are loaded: the Select resolves each selected id against
      // `options`, and for an id it can't find it falls back to rendering the *entire* joined value
      // string as a chip. Mounting it with the value before options arrive triggers that fallback
      // and leaves bogus "id,id,id" chips behind, so we wait for the options first.
      /* @__PURE__ */ React.createElement(Spinner, { text: "Loading event definitions..." })
    ) : /* @__PURE__ */ React.createElement(
      Select,
      {
        inputId: "event-definitions-select",
        value: ((_a = field.value) != null ? _a : []).filter((id) => options.some((option) => option.value === id)).join(","),
        placeholder: "Select event definitions...",
        options,
        multi: true,
        onChange: (values) => helpers.setValue(values ? values.split(",").filter(Boolean) : [])
      }
    )
  );
};
const MAX_URL_LENGTH = 2048;
const validate = (values) => {
  var _a, _b, _c;
  const errors = {};
  if (!((_a = values.name) == null ? void 0 : _a.trim())) errors.name = "Required";
  const url = (_b = values.reference_url) == null ? void 0 : _b.trim();
  if (url) {
    if (url.length > MAX_URL_LENGTH) {
      errors.reference_url = `Must not exceed ${MAX_URL_LENGTH} characters`;
    } else if (!isValidURL(url) || !hasAcceptedProtocol(url)) {
      errors.reference_url = "Must be a valid HTTP(S) URL";
    }
  }
  const createdAt = (_c = values.created_at) == null ? void 0 : _c.trim();
  if (createdAt && !isValidDateInput(createdAt)) {
    errors.created_at = `Must be a valid date (${DATE_TIME_FORMATS.date})`;
  }
  return errors;
};
const DetectionChainModal = ({ onClose, chain = void 0 }) => {
  const isEdit = !!chain;
  const { mutateAsync: createChain, isPending: creating } = useCreateDetectionChain();
  const { mutateAsync: updateChain, isPending: updating } = useUpdateDetectionChain();
  const saving = creating || updating;
  const { data: streams } = useAllStreams();
  const streamCategoryOptions = [...new Set((streams != null ? streams : []).flatMap((stream) => {
    var _a;
    return (_a = stream.categories) != null ? _a : [];
  }))].sort(
    (a, b) => a.localeCompare(b)
  );
  const onSubmit = async (values) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _i;
    const payload = {
      name: values.name.trim(),
      description: ((_a = values.description) == null ? void 0 : _a.trim()) || null,
      threat_actor: ((_b = values.threat_actor) == null ? void 0 : _b.trim()) || null,
      targeted_technologies: (_c = values.targeted_technologies) != null ? _c : [],
      industries: (_d = values.industries) != null ? _d : [],
      stream_categories: (_e = values.stream_categories) != null ? _e : [],
      source: ((_f = values.source) == null ? void 0 : _f.trim()) || null,
      reference_url: ((_g = values.reference_url) == null ? void 0 : _g.trim()) || null,
      created_at: ((_h = values.created_at) == null ? void 0 : _h.trim()) || null,
      event_definition_ids: (_i = values.event_definition_ids) != null ? _i : []
    };
    try {
      if (isEdit) {
        await updateChain({ id: chain.id, payload });
      } else {
        await createChain(payload);
      }
      onClose();
    } catch {
    }
  };
  return /* @__PURE__ */ React.createElement(Formik, { initialValues: initialValues(chain), validate, onSubmit }, ({ isValid }) => /* @__PURE__ */ React.createElement(Modal, { show: true, onHide: onClose }, /* @__PURE__ */ React.createElement(Form, null, /* @__PURE__ */ React.createElement(Modal.Header, null, /* @__PURE__ */ React.createElement(Modal.Title, null, isEdit ? "Edit Detection Chain" : "Create Detection Chain")), /* @__PURE__ */ React.createElement(Modal.Body, null, /* @__PURE__ */ React.createElement(FormikInput, { id: "name", name: "name", label: "Name", required: true }), /* @__PURE__ */ React.createElement(FormikInput, { id: "description", name: "description", label: "Description", type: "textarea", rows: 3 }), /* @__PURE__ */ React.createElement(FormikInput, { id: "threat_actor", name: "threat_actor", label: "Threat Actor" }), /* @__PURE__ */ React.createElement(
    StringListField,
    {
      name: "targeted_technologies",
      label: "Targeted Technologies",
      help: "Technologies this chain targets, e.g. Windows, Linux, Android.",
      placeholder: "Add a technology..."
    }
  ), /* @__PURE__ */ React.createElement(
    StringListField,
    {
      name: "industries",
      label: "Industries",
      help: "Industries this chain is relevant to, e.g. Education, Government.",
      placeholder: "Add an industry..."
    }
  ), /* @__PURE__ */ React.createElement(
    StringListField,
    {
      name: "stream_categories",
      label: "Stream Categories",
      help: "The stream categories this chain's event definitions typically originate from, e.g. Firewall, Phishing. For informational purposes only.",
      placeholder: "Add a stream category...",
      suggestions: streamCategoryOptions
    }
  ), /* @__PURE__ */ React.createElement(EventDefinitionsField, null), /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      id: "source",
      name: "source",
      label: "Source",
      help: "Author or publisher of the source material this chain is based on."
    }
  ), /* @__PURE__ */ React.createElement(CreatedDateField, null), /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      id: "reference_url",
      name: "reference_url",
      label: "Reference URL",
      help: "Link to the original source material (e.g. the blog post)."
    }
  )), /* @__PURE__ */ React.createElement(Modal.Footer, null, /* @__PURE__ */ React.createElement(
    ModalSubmit,
    {
      onCancel: onClose,
      disabledSubmit: !isValid || saving,
      submitButtonType: "submit",
      submitButtonText: isEdit ? "Update Detection Chain" : "Create Detection Chain"
    }
  )))));
};
export default DetectionChainModal;
